// Landing-page translations — Dutch + English.
// Strings only; icons + image URLs live in /components/landing/constants.js
// and are zipped by array index where needed.

export const LANDING_I18N = {
  en: {
    navbar: {
      whatWeBuild: 'What we build',
      whereWeWork: 'Where we work',
      howWeWork: 'How we work',
      products: 'Products',
      platform: 'Platform',
      flowcrew: 'FlowCrew',
      modules: 'Modules',
      madeBy: 'Made by',
      trialButton: 'Start free trial',
      contact: 'Contact',
      loginButton: 'Log into Kanriva™',
      loginShort: 'Log in',
      peekButton: 'Take a Peek',
      walkthroughShort: 'Book a walkthrough',
      security: 'Security',
      whoIsWho: 'Who is who',
      languageNote: 'Support and live sessions are in English · Dutch in writing',
    },
    hero: {
      eyebrow: 'Kanriva™ · live in production',
      titleA: 'Your operation,',
      titleB: 'under',
      titleC: 'control',
      lede: 'Workload-driven workforce planning and operational performance — for warehouses and teams that run on shifts.',
      description:
        'Plan the week on your real workload, publish it straight to every worker\'s phone, measure what actually happened on the floor — then improve it, week after week. One login instead of a folder of spreadsheets, exports that fit your reporting, and a mobile companion app for the crew.',
      trustBadge: '17+ years on the floor',
      trustSub: 'Built by Kaizen Kanri — logistics, sales and management, lived, not consulted.',
      ctaWalkthrough: 'Book a 30-minute operational walkthrough',
      ctaLogin: 'Log in to Kanriva™',
      ctaPeek: 'Take a Peek',
      ctaTrialSmall: 'or start a 14-day trial',
      ctaLoginSmall: 'log in',
      languageNote: 'We work in English. Dutch is welcome in writing.',
      languageNoteLink: 'Read why →',
      trustLine: 'EU-hosted · role-based access · full change history · processing agreement and security pack downloadable before you enter any employee data.',
      trustLink: 'Security & GDPR',
      industriesLabel: 'Where Kanriva™ runs',
    },
    features: {
      eyebrow: 'What we build',
      title: 'Software tailored to one operation at a time.',
      subtitle:
        'Every engagement starts the same way: a walk through the operation, a notebook, a list of friction points the team already knows. Then we build the smallest thing that fixes the biggest of those, and iterate from there.',
      // Order matches `services` array in constants.js
      items: [
        { title: 'Custom operations software', description: 'Tools shaped around how your team already works. Web-based, mobile-friendly, no licences-per-seat lock-in.' },
        { title: 'Logistics & planning platforms', description: 'Inbound, outbound, shifts, KPIs — connected views that replace the spreadsheet pile your shift leads keep emailing each other.' },
        { title: 'Spreadsheets to real systems', description: 'When the master file is too big to open and one wrong cell breaks payroll, it is time. We migrate the logic without losing the history.' },
        { title: 'Process optimisation', description: 'Lean and Kaizen applied where it matters: on the floor, with the people doing the work. No theatre, no posters.' },
        { title: 'Dashboards & live collaboration', description: 'Numbers your team can act on, refreshed in real time, with sensible defaults so a new hire can read them on day one.' },
        { title: 'Workflow design', description: 'Validations, approvals, handovers — the boring parts that decide whether your operation runs quietly or noisily.' },
      ],
    },
    industries: {
      eyebrow: 'Where we work',
      title: 'Wherever the operation lives in spreadsheets.',
      subtitle:
        'Industries change but the symptoms repeat: too many tabs, missing audit trails, a key person who knows the macros. Kaizen Kanri ships across logistics, warehousing, healthcare and retail because the underlying problem is the same operating muscle, badly supported.',
      // Order matches `industries` array in constants.js
      names: ['Logistics & supply chain', 'Warehousing & operations', 'Healthcare & services', 'Retail & wholesale'],
      sub: 'Tailored to fit',
      badgeTitle: 'One team',
      badgeSub: 'designing, building, supporting',
    },
    benefits: {
      eyebrow: 'How we work',
      title: 'Small steps, measurable gains.',
      subtitle:
        'Kaizen Kanri runs lean engagements with a fixed scope. A pilot lands quickly, the floor reacts, the build adjusts. Scope and delivery dates are agreed up front, and what is not in the pilot is named rather than promised.',
      reasons: [
        'Tailored to your operation — no off-the-shelf compromises',
        '17+ years of logistics, sales and management behind every build',
        'Direct access to the people who design and support the platform',
        'Built for the floor, validated by the floor',
        'Fixed-scope pilots — you see results before you scale',
        'Excel and PDF exports stay first-class citizens',
        'Your data stays in the EU, with role-based access and a full change history',
        'Existing tools (SAP, payroll, scanners) plug in, not get replaced',
      ],
      cards: [
        { title: 'Understand', sub: 'Walk the floor before drawing a single screen.' },
        { title: 'Design what fits', sub: 'A workflow that matches your operation, not a generic tool.' },
        { title: 'Build & implement', sub: 'Web-first, user-friendly, ready to scale.' },
        { title: 'Improve', sub: 'Continuous adjustments — Kaizen, in practice.' },
      ],
    },
    products: {
      eyebrow: 'The platform',
      title: 'Three production apps. Yours could be the fourth.',
      kanrivaHeader: {
        eyebrow: 'The platform',
        title: 'Kanriva™',
        subtitle: 'One place for planning, measuring and improving the work — built on the floor, for the floor. Live in production every day, in a real warehouse operation.',
      },
      flowcrewHeader: {
        eyebrow: 'The mobile half of Kanriva™',
        title: 'And the crew gets its own app.',
        subtitle: 'Publishing a schedule in Kanriva™ puts it straight into the pocket of every worker — in their own language, on their own phone.',
      },
      alsoHeader: {
        eyebrow: 'Another product by Kaizen Kanri · not part of Kanriva™',
        title: 'BBMI-OAI — the paper logbook, replaced.',
        subtitle: 'Same approach, different industry: a compliance-grade digital logbook for fire-detection installations, live with installers, building owners and inspectors.',
      },
      subtitle:
        'All three applications below were designed, built and now operated by Kaizen Kanri. They are working proof of how a single, tailored platform — web or mobile — replaces a folder of spreadsheets and a wall of paper. The next one is the one we build for your operation.',
      liveBadge: 'Live in production',
      kaizen: {
        title: 'Kanriva™',
        tagline: 'One platform. Every shift, every pick, every number — under control.',
        description:
          'Kanriva™ is the operating system for organisations that run on shifts, volume and deadlines. It plans the workforce against real workload, measures what actually happened on the floor, and turns the gap between the two into concrete improvement — in one login, with one source of truth. Planners stop rebuilding spreadsheets, team leads stop guessing, and management finally reads the same numbers as the floor.',
        bullets: [
          'Plans the week against the actual workload — volume in, required hours out, an explainable staffing recommendation per department that the planner confirms, published live to every worker\'s phone.',
          'Measures the floor in real time: performance per person and per process, benchmarks, plan-versus-actual, overtime and surcharge bands calculated to the minute.',
          'Runs continuous improvement end-to-end — improvement projects, audits, process mapping, action registers, goals and KPI dashboards that update themselves.',
          'Keeps the operation accountable: shift handovers, absence and holiday control, documented personnel follow-up with role-controlled access, and a complete change history.',
          'Fits how you already work — multiple sites, departments and client operations, access restricted per role and per module, Excel and PDF exports everywhere, your data in the EU.',
        ],
        modules: [
          'Improvement projects & Kaizen',
          'Audits & smart checklists',
          'Workforce & shift planning',
          'Live KPI dashboards',
          'Performance analytics',
          'Outbound & damage control',
          'Shift handover & HR files',
          'Process mapping & value streams',
        ],
        cta: 'Log into Kanriva™',
        screenshotDashboard: 'Dashboard · demo data',
        screenshotKpis: 'KPI analytics · demo data',
      },
      bmi: {
        title: 'BBMI-OAI — Digital Logbook',
        description:
          'The digital logbook for fire detection installations (BMA/OAI). NEN 2654-1 compliant inspections, fault registration with assisted analysis of earlier incidents, a QR inspection link for the fire department, and real-time compliance insight — available in Dutch, English, German and French.',
        bullets: [
          "Built for installers, OBE's, building owners and inspectors.",
          'NEN 2654-1 inspections, fault & maintenance log, audit trail.',
          'Assisted fault analysis surfaces earlier, similar incidents to speed up diagnosis.',
          'Multilingual: NL · EN · DE · FR.',
        ],
        modules: [
          'NEN 2654-1 inspections',
          'Assisted fault analysis',
          'QR link for the fire department',
          'Compliance dashboard',
        ],
        cta: 'Open the app',
        ctaContact: 'Ask about BBMI-OAI',
        ctaPlay: 'Get it on Google Play',
        ctaPlayPending: 'Coming to Google Play',
        iosNote: 'iOS under consideration',
        tileEyebrow: 'Brandmeldinstallatie',
        tileTitle: 'BBMI-OAI · Digital Logbook',
        tileSub: 'The paper folder, replaced · NEN 2654-1 by default.',
      },
      flowcrew: {
        eyebrow: 'Part of Kanriva™ · Mobile companion',
        title: 'FlowCrew — the worker companion app for Kanriva™',
        description:
          'Your schedule, your team and your planner — in your pocket, in your language. Kanriva™ gives planners a powerful web dashboard; FlowCrew gives every warehouse worker a simple app on their own phone, designed to need no formal training, in nine languages. The moment a planner publishes the schedule it is live on every phone, and every chat or request is tied to a specific day — so nobody has to ask "which Friday did you mean?"',
        bullets: [
          'My Planning — a month calendar, colour-coded at a glance: green = planned (with the start time in the day box), blue = approved holiday, grey = free.',
          'Team — who is in today, one day at a time. Workers see their own department; team leads and planners see every department.',
          'Discuss schedule — one tap on a day opens a live chat with the planner about that exact shift.',
          'Requests — a day off, a holiday range or a shift change, decided in the planner\'s inbox and pushed straight back to the worker.',
          'Nine languages (EN · NL · PL · RO · BG · TR · ES · PT · UA), live notifications, live on Google Play for Android — iOS under consideration.',
        ],
        modules: [
          'Month planning calendar',
          'Team · who is in today',
          'Day-linked chat & requests',
          '9 languages · Android (Play Store)',
        ],
        cta: 'Open the app',
        ctaContact: 'Ask about FlowCrew',
        ctaPlay: 'Get it on Google Play',
        ctaPlayPending: 'Coming soon on Google Play',
        iosNote: 'iOS under consideration',
        ctaAppPending: 'App launching soon',
        attribution: 'Part of the Kanriva™ platform · built by Kaizen Kanri',
      },
      cta: {
        title: 'Your operation is next.',
        sub: 'All three apps above started as a notebook, a walk-through and a list of friction points the team already knew. Yours can start the same way — tailored to your workflow, grounded in 17+ years of logistics, sales and management experience.',
        button: 'Ask about a tailored build',
      },
    },
    creator: {
      eyebrow: 'Why Kaizen Kanri',
      title: 'Software built on 17+ years of running the work.',
      lead:
        'Most operational software comes from teams that have never run a shift. Kaizen Kanri does the opposite. Every application is grounded in more than seventeen years of hands-on experience across logistics, sales and management — the three angles every operation has to balance at once.',
      pillars: [
        { title: 'Logistics & operations', sub: 'Inbound, outbound, picking, expedition — the daily reality of running a floor.' },
        { title: 'Sales', sub: 'What customers actually ask for, why deals stall, where margins quietly leak.' },
        { title: 'Management', sub: 'Building, leading and steering teams that have to deliver, week after week.' },
      ],
      paragraphs: [
        'Every build starts on site. Notes from dispatchers, schedulers, pickers and managers come first; only then are the screens designed. The result is software that fits the rhythm of the operation — quieter days, fewer late evenings, less time spent reconciling versions of the same spreadsheet.',
        'The platforms are web-based, mobile-friendly, and built to slot in alongside the systems already in place. Excel and PDF exports remain first-class so finance, HR and steering committees keep working the way they always have.',
      ],
      closer:
        'Tailored to your specific needs — no generic toolkits, no template projects. The scope is yours, the workflow is yours, the software adapts to both.',
    },
    contact: {
      eyebrow: "Let's Connect",
      language: {
        eyebrow: 'Language & communication',
        title: 'We work in English — and we say that openly.',
        body:
          'Kaizen Kanri is a multinational, multicultural project. Kanriva™ itself, the walkthroughs, onboarding, training and day-to-day support are delivered in English, because that is the language our team and our customers genuinely share.',
        bullets: [
          { title: 'Registered and operating in the Netherlands', sub: 'A Dutch company, working to Dutch and EU rules — including the CAO logic built into the planning engine.' },
          { title: 'Dutch in writing, today', sub: 'E-mail, documentation, contracts and the processing agreement are available in Dutch. The platform interface ships in English and Dutch.' },
          { title: 'Spoken Dutch: not yet a promise', sub: 'Live calls, demos and support are held in English. We would rather tell you that now than let you discover it in the middle of a rollout.' },
        ],
        closing:
          'If English is workable for the people who will use the system, nothing stands in your way — most teams find the product vocabulary is short and operational. Should that not fit your operation, tell us plainly and we will say honestly whether we are the right partner today.',
        announce: 'When spoken Dutch support becomes available, it will be announced on this page first.',
      },
      titleA: 'Start the',
      titleB: 'conversation.',
      lead:
        'Tell Kaizen Kanri what your operation is wrestling with. Replies normally arrive within one business day, with a direct answer instead of a brochure. The first meeting is free, the rest is up to you.',
      trust: [
        { title: 'Reply within one business day', sub: 'Usually faster during CET business hours — written by the person who builds the platform.' },
        { title: '30-minute scoping call', sub: 'Walk through the current operation on a screen-share, held in English. No slides, no sales script.' },
        { title: 'Pilot-friendly engagements', sub: 'Start small, scale only when the numbers earn it.' },
      ],
      cardStatus: 'Available now · reply within one business day',
      cardTitle: 'Pick your channel',
      cardSub:
        'One click and your favourite app opens with a friendly opener already written. No web forms, no waiting on a server somewhere — the message lands directly in the inbox that gets read.',
      emailTitle: 'Send an email',
      emailSub: 'Opens your email app with everything pre-filled',
      whatsappTitle: 'WhatsApp',
      whatsappSub: 'Quickest channel for a short question',
      footnote: 'No forms, no spam, no newsletters. One real reply, written by hand.',
      mailSubject: 'Conversation with Kaizen Kanri',
      mailBody: [
        'Hello,',
        '',
        "I'd like to talk about a project with Kaizen Kanri.",
        '',
        '• Operation type: ',
        '• Team size: ',
        '• Current pain point: ',
        '• Outcome hoped for: ',
        '',
        'Thanks!',
      ].join('\n'),
      whatsappGreeting: "Hello, I'd like to talk about a project with Kaizen Kanri.",
    },
    footer: {
      tagline: 'Continuous improvement, applied with purpose.',
      terms: 'Terms & Conditions',
      privacy: 'Privacy Policy',
      security: 'Security & GDPR',
      method: 'KaizenKanri Method™ — all rights reserved.',
      language: 'Registered and operating in the Netherlands. Product, live sessions and support are in English; Dutch correspondence, documentation and contracts are available in writing. Any change to this will be announced on this site.',
      trademark: 'Kanriva™ is a trademark of Kaizen Kanri. FlowCrew™ is a trademark of Kaizen Kanri.',
    },
  },

  nl: {
    navbar: {
      whatWeBuild: 'Wat we bouwen',
      whereWeWork: 'Waar we werken',
      howWeWork: 'Hoe we werken',
      products: 'Producten',
      platform: 'Platform',
      flowcrew: 'FlowCrew',
      modules: 'Modules',
      madeBy: 'Gemaakt door',
      trialButton: 'Start gratis proef',
      contact: 'Contact',
      loginButton: 'Inloggen op Kanriva™',
      loginShort: 'Inloggen',
      peekButton: 'Kijk binnen',
      walkthroughShort: 'Plan een walkthrough',
      security: 'Beveiliging',
      whoIsWho: 'Wie is wie',
      languageNote: 'Support en live sessies in het Engels · Nederlands schriftelijk',
    },
    hero: {
      eyebrow: 'Kanriva™ · live in productie',
      titleA: 'Uw operatie,',
      titleB: 'onder',
      titleC: 'controle',
      lede: 'Personeelsplanning op werkelijke werklast en operationele prestatiemeting — voor magazijnen en teams die op diensten draaien.',
      description:
        'Plan de week op uw échte werklast, publiceer die direct naar de telefoon van elke medewerker, meet wat er werkelijk op de vloer gebeurde — en verbeter het, week na week. Eén login in plaats van een map vol spreadsheets, exports die in uw rapportage passen, en een mobiele app voor het team.',
      trustBadge: '17+ jaar op de werkvloer',
      trustSub: 'Gebouwd door Kaizen Kanri — logistiek, sales en management, zelf gedaan.',
      ctaWalkthrough: 'Plan een walkthrough van 30 minuten',
      ctaLogin: 'Inloggen op Kanriva™',
      ctaPeek: 'Kijk binnen',
      ctaTrialSmall: 'of start een proefperiode van 14 dagen',
      ctaLoginSmall: 'inloggen',
      languageNote: 'We werken in het Engels. Nederlands mag schriftelijk.',
      languageNoteLink: 'Lees waarom →',
      trustLine: 'EU-hosting · rechten per rol · volledige wijzigingshistorie · verwerkersovereenkomst en security-pack te downloaden vóórdat u personeelsdata invoert.',
      trustLink: 'Beveiliging & AVG',
      industriesLabel: 'Waar Kanriva™ draait',
    },
    features: {
      eyebrow: 'Wat we bouwen',
      title: 'Software op maat, één operatie tegelijk.',
      subtitle:
        'Elk traject begint hetzelfde: een rondgang door de operatie, een notitieboekje, een lijstje frictiepunten die het team al kent. Daarna bouwen we het kleinste dat het grootste probleem oplost, en itereren we vanaf daar.',
      items: [
        { title: 'Operationele software op maat', description: 'Tools rondom de manier waarop uw team al werkt. Webgebaseerd, geschikt voor mobiel, geen licenties-per-seat lock-in.' },
        { title: 'Logistiek- en planningsplatforms', description: 'Inkomend, uitgaand, diensten, KPI\'s — gekoppelde overzichten die de stapel Excel-bestanden vervangen die uw teamleiders elkaar mailen.' },
        { title: 'Van spreadsheets naar systemen', description: 'Als het masterbestand te groot is om te openen en één foute cel de loonadministratie breekt, is het tijd. We zetten de logica over zonder de historie te verliezen.' },
        { title: 'Procesoptimalisatie', description: 'Lean en Kaizen toegepast waar het ertoe doet: op de vloer, met de mensen die het werk doen. Geen toneelstuk, geen posters.' },
        { title: 'Dashboards en live samenwerking', description: 'Cijfers waar uw team mee aan de slag kan, real-time ververst, met sensible defaults zodat een nieuwe collega ze op dag één leest.' },
        { title: 'Workflow-ontwerp', description: 'Validaties, goedkeuringen, overdrachten — de saaie onderdelen die bepalen of uw operatie rustig of luidruchtig draait.' },
      ],
    },
    industries: {
      eyebrow: 'Waar we werken',
      title: 'Overal waar de operatie in spreadsheets leeft.',
      subtitle:
        'Sectoren verschillen, de symptomen herhalen zich: te veel tabbladen, ontbrekende audit trails, één sleutelfiguur die de macro\'s kent. Kaizen Kanri levert in logistiek, magazijn, zorg en retail omdat de onderliggende uitdaging dezelfde is: operationele spierkracht, slecht ondersteund.',
      names: ['Logistiek & supply chain', 'Magazijn & operatie', 'Zorg & dienstverlening', 'Retail & groothandel'],
      sub: 'Op maat ingericht',
      badgeTitle: 'Eén team',
      badgeSub: 'ontwerpt, bouwt, ondersteunt',
    },
    benefits: {
      eyebrow: 'Hoe we werken',
      title: 'Kleine stappen, meetbare winst.',
      subtitle:
        'Kaizen Kanri werkt in korte cycli met een vaste scope. Een pilot is er snel, de vloer reageert, de bouw past zich aan. Scope en opleverdata spreken we vooraf af, en wat niet in de pilot zit wordt benoemd in plaats van belooft.',
      reasons: [
        'Afgestemd op uw operatie — geen standaardpakketten waar u zich naar moet plooien',
        '17+ jaar ervaring in logistiek, sales en management achter elke bouw',
        'Direct contact met de mensen die het platform ontwerpen en ondersteunen',
        'Gebouwd voor de vloer, gevalideerd door de vloer',
        'Pilots met vaste scope — u ziet resultaat vóór u opschaalt',
        'Excel- en PDF-exports blijven first-class citizens',
        'Uw data blijft in de EU, met rechten per rol en een volledige wijzigingshistorie',
        'Bestaande tools (SAP, payroll, scanners) sluiten aan, worden niet vervangen',
      ],
      cards: [
        { title: 'Begrijpen', sub: 'Eerst de vloer op, dan pas een scherm tekenen.' },
        { title: 'Passend ontwerpen', sub: 'Een werkwijze die past bij uw operatie, geen generieke tool.' },
        { title: 'Bouwen & implementeren', sub: 'Web-first, gebruiksvriendelijk, klaar om op te schalen.' },
        { title: 'Verbeteren', sub: 'Continue bijstelling — Kaizen, in de praktijk.' },
      ],
    },
    products: {
      eyebrow: 'Het platform',
      title: 'Drie productie-apps. De uwe wordt de vierde.',
      kanrivaHeader: {
        eyebrow: 'Het platform',
        title: 'Kanriva™',
        subtitle: 'Eén plek om het werk te plannen, te meten en te verbeteren — gebouwd op de vloer, voor de vloer. Elke dag live in een echte magazijnoperatie.',
      },
      flowcrewHeader: {
        eyebrow: 'De mobiele helft van Kanriva™',
        title: 'En het team krijgt zijn eigen app.',
        subtitle: 'Een rooster publiceren in Kanriva™ zet het direct in de broekzak van elke medewerker — in de eigen taal, op de eigen telefoon.',
      },
      alsoHeader: {
        eyebrow: 'Nog een product van Kaizen Kanri · geen onderdeel van Kanriva™',
        title: 'BBMI-OAI — de papieren map, vervangen.',
        subtitle: 'Dezelfde aanpak, andere branche: een compliance-vast digitaal logboek voor brandmeldinstallaties, live bij installateurs, gebouweigenaren en inspecteurs.',
      },
      subtitle:
        'Alle drie de onderstaande applicaties zijn ontworpen, gebouwd en worden nu beheerd door Kaizen Kanri. Ze zijn het werkende bewijs dat één op maat gebouwd platform — web of mobiel — een mapje spreadsheets en een wand vol papier vervangt. De volgende — dat is degene die we voor uw operatie bouwen.',
      liveBadge: 'Live in productie',
      kaizen: {
        title: 'Kanriva™',
        tagline: 'Eén platform. Elke dienst, elke pick, elk cijfer — onder controle.',
        description:
          'Kanriva™ is het besturingssysteem voor organisaties die draaien op diensten, volume en deadlines. Het plant de bezetting op de échte werklast, meet wat er op de vloer werkelijk gebeurt en maakt van het verschil daartussen concrete verbetering — in één login, met één bron van waarheid. Planners bouwen geen spreadsheets meer, teamleiders hoeven niet te gokken en de directie leest eindelijk dezelfde cijfers als de vloer.',
        bullets: [
          'Plant de week op de werkelijke werklast — volume erin, benodigde uren eruit, een uitlegbaar bezettingsadvies per afdeling dat de planner bevestigt, live gepubliceerd op de telefoon van elke medewerker.',
          'Meet de vloer in realtime: prestaties per persoon en per proces, benchmarks, plan versus werkelijk, overwerk en toeslagstaffels tot op de minuut berekend.',
          'Voert continu verbeteren van begin tot eind: verbeterprojecten, audits, processchema\'s, actieregisters, doelen en KPI-dashboards die zichzelf bijwerken.',
          'Houdt de operatie aantoonbaar op orde: dienstoverdrachten, verzuim- en vakantiebeheer, vastgelegde personeelsopvolging met rechten per rol, en een volledige wijzigingshistorie.',
          'Sluit aan op hoe u al werkt — meerdere locaties, afdelingen en klantoperaties, rechten per rol en per module, overal Excel- en PDF-exports, uw data binnen de EU.',
        ],
        modules: [
          'Verbeterprojecten & Kaizen',
          'Audits & slimme checklists',
          'Personeels- & dienstplanning',
          'Live KPI-dashboards',
          'Prestatie-analyses',
          'Uitgaande stroom & schadebeheer',
          'Dienstoverdracht & personeelsdossier',
          'Processchema\'s & waardestromen',
        ],
        cta: 'Inloggen op Kanriva™',
        screenshotDashboard: 'Dashboard · demo-data',
        screenshotKpis: 'KPI-analyses · demo-data',
      },
      bmi: {
        title: 'BBMI-OAI — Digitaal Logboek',
        description:
          'Het digitale logboek voor brandmeldinstallaties (BMA/OAI). NEN 2654-1-conforme controles, storingsregistratie met ondersteunde analyse van eerdere storingen, een QR-inspectielink voor de brandweer en realtime compliance-inzicht — beschikbaar in het Nederlands, Engels, Duits en Frans.',
        bullets: [
          'Gebouwd voor installateurs, OBE\'s, gebouweigenaren én inspecteurs.',
          'NEN 2654-1-controles, storings- en onderhoudslog, audit trail.',
          'Ondersteunde storingsanalyse laat eerdere, vergelijkbare storingen zien en versnelt zo de diagnose.',
          'Meertalig: NL · EN · DE · FR.',
        ],
        modules: [
          'NEN 2654-1 controles',
          'Ondersteunde storingsanalyse',
          'QR-link voor de brandweer',
          'Compliance-dashboard',
        ],
        cta: 'Open de app',
        ctaContact: 'Vraag naar BBMI-OAI',
        ctaPlay: 'Beschikbaar op Google Play',
        ctaPlayPending: 'Binnenkort op Google Play',
        iosNote: 'iOS wordt overwogen',
        tileEyebrow: 'Brandmeldinstallatie',
        tileTitle: 'BBMI-OAI · Digitaal Logboek',
        tileSub: 'De papieren map, vervangen · NEN 2654-1 by default.',
      },
      flowcrew: {
        eyebrow: 'Onderdeel van Kanriva™ · Mobiele companion',
        title: 'FlowCrew — de companion-app voor uw medewerkers',
        description:
          'Uw rooster, uw team en uw planner — in uw broekzak, in uw eigen taal. Kanriva™ geeft planners een krachtig webdashboard; FlowCrew geeft elke magazijnmedewerker een simpele app op de eigen telefoon, die geen formele training vraagt, in negen talen. Zodra de planner het rooster publiceert staat het live op elke telefoon, en elk gesprek of verzoek hangt aan een concrete dag — dus niemand hoeft nog te vragen "welke vrijdag bedoel je?"',
        bullets: [
          'Mijn planning — een maandkalender die in één blik alles vertelt: groen = ingepland (met de starttijd in het dagvak), blauw = goedgekeurde vakantie, grijs = vrij.',
          'Team — wie is er vandaag, dag voor dag. Medewerkers zien hun eigen afdeling; teamleiders en planners zien alle afdelingen.',
          'Rooster bespreken — één tik op een dag opent een live chat met de planner over precies die dienst.',
          'Verzoeken — een dag vrij, een vakantieperiode of een dienstwijziging: beslist in de inbox van de planner en direct terug bij de medewerker.',
          'Negen talen (EN · NL · PL · RO · BG · TR · ES · PT · UA), live meldingen, nu live in de Google Play Store voor Android — iOS wordt overwogen.',
        ],
        modules: [
          'Maandkalender planning',
          'Team · wie is er vandaag',
          'Chat & verzoeken per dag',
          '9 talen · Android (Play Store)',
        ],
        cta: 'Open de app',
        ctaContact: 'Vraag naar FlowCrew',
        ctaPlay: 'Beschikbaar op Google Play',
        ctaPlayPending: 'Binnenkort op Google Play',
        iosNote: 'iOS wordt overwogen',
        ctaAppPending: 'App komt binnenkort',
        attribution: 'Onderdeel van het Kanriva™-platform · gebouwd door Kaizen Kanri',
      },
      cta: {
        title: 'Uw operatie is de volgende.',
        sub: 'Alle drie de apps hierboven begonnen als een notitieboekje, een rondgang en een lijstje frictiepunten dat het team al kende. Dat van u kan op precies dezelfde manier beginnen — op maat van uw werkwijze, met 17+ jaar ervaring in logistiek, sales en management als fundament.',
        button: 'Vraag naar een bouw op maat',
      },
    },
    creator: {
      eyebrow: 'Waarom Kaizen Kanri',
      title: 'Software gebouwd op 17+ jaar in het echte werk.',
      lead:
        'De meeste operationele software komt van teams die nooit een dienst hebben gedraaid. Kaizen Kanri doet het andersom. Elke applicatie staat op meer dan zeventien jaar hands-on ervaring in logistiek, sales en management — de drie invalshoeken die elke operatie tegelijkertijd moet balanceren.',
      pillars: [
        { title: 'Logistiek & operatie', sub: 'Inkomend, uitgaand, picking, expeditie — de dagelijkse realiteit van een vloer.' },
        { title: 'Sales', sub: 'Wat klanten echt vragen, waar deals stranden, waar marge stil weglekt.' },
        { title: 'Management', sub: 'Teams bouwen, leiden en sturen die week in week uit moeten leveren.' },
      ],
      paragraphs: [
        'Elke bouw begint op locatie. Eerst de aantekeningen van planners, ploegleiders, pickers en managers; pas daarna komt het schermontwerp. Het resultaat is software die meebeweegt met het ritme van de operatie — rustigere dagen, minder late avonden, minder tijd kwijt aan het verzoenen van verschillende versies van hetzelfde spreadsheet.',
        'De platforms zijn webgebaseerd, geschikt voor mobiel, en gebouwd om naast de bestaande systemen te draaien. Excel- en PDF-exports blijven first-class, zodat finance, HR en stuurgroepen kunnen blijven werken zoals ze gewend zijn.',
      ],
      closer:
        'Afgestemd op uw specifieke wensen — geen generieke toolkits, geen sjabloonprojecten. De scope is van u, de werkwijze is van u, de software past zich aan beide aan.',
    },
    contact: {
      eyebrow: 'Maak contact',
      language: {
        eyebrow: 'Taal & communicatie',
        title: 'Wij werken in het Engels — en zeggen dat open.',
        body:
          'Kaizen Kanri is een multinationaal, multicultureel project. Kanriva™ zelf, de walkthroughs, onboarding, training en dagelijkse support zijn in het Engels, omdat dat de taal is die ons team en onze klanten werkelijk delen.',
        bullets: [
          { title: 'Geregistreerd en actief in Nederland', sub: 'Een Nederlands bedrijf, werkend volgens Nederlandse en EU-regels — inclusief de CAO-logica in de planningsmodule.' },
          { title: 'Nederlands schriftelijk, nu al', sub: 'E-mail, documentatie, contracten en de verwerkersovereenkomst zijn in het Nederlands beschikbaar. De interface is er in het Engels en het Nederlands.' },
          { title: 'Nederlands gesproken: nog geen belofte', sub: 'Gesprekken, demo\'s en support gaan in het Engels. Dat zeggen we liever nu dan dat u het midden in een uitrol ontdekt.' },
        ],
        closing:
          'Als Engels werkbaar is voor de mensen die met het systeem werken, staat niets u in de weg — de vaktaal in het product is kort en operationeel. Past dat niet bij uw operatie, zeg het gewoon: dan zeggen wij eerlijk of wij vandaag de juiste partner zijn.',
        announce: 'Zodra Nederlandstalige support mondeling beschikbaar is, wordt dat hier als eerste aangekondigd.',
      },
      titleA: 'Begin het',
      titleB: 'gesprek.',
      lead:
        'Vertel Kaizen Kanri waar uw operatie mee worstelt. Reacties komen normaal gesproken binnen één werkdag, met een direct antwoord in plaats van een brochure. Het eerste gesprek is vrijblijvend, de rest bepaalt u zelf.',
      trust: [
        { title: 'Reactie binnen één werkdag', sub: 'Vaak sneller tijdens kantooruren (CET) — geschreven door degene die het platform bouwt.' },
        { title: 'Scoping-gesprek van 30 minuten', sub: 'Samen door uw huidige operatie via een screenshare, in het Engels. Geen slides, geen verkoopscript.' },
        { title: 'Pilot-vriendelijke trajecten', sub: 'Klein beginnen, opschalen alleen als de cijfers het verdienen.' },
      ],
      cardStatus: 'Nu beschikbaar · reactie binnen één werkdag',
      cardTitle: 'Kies uw kanaal',
      cardSub:
        'Eén klik en uw favoriete app opent met een nette opening al klaar. Geen webformulieren, geen wachten op een server ergens — het bericht komt direct in de inbox die gelezen wordt.',
      emailTitle: 'Stuur een e-mail',
      emailSub: 'Opent uw e-mailapp met alles ingevuld',
      whatsappTitle: 'WhatsApp',
      whatsappSub: 'Snelste kanaal voor een korte vraag',
      footnote: 'Geen formulieren, geen spam, geen nieuwsbrieven. Eén echte reactie, met de hand getypt.',
      mailSubject: 'Gesprek met Kaizen Kanri',
      mailBody: [
        'Hallo,',
        '',
        'Ik wil graag een project met Kaizen Kanri bespreken.',
        '',
        '• Type operatie: ',
        '• Teamomvang: ',
        '• Grootste knelpunt op dit moment: ',
        '• Gewenst resultaat: ',
        '',
        'Bedankt!',
      ].join('\n'),
      whatsappGreeting: 'Hallo, ik wil graag een project met Kaizen Kanri bespreken.',
    },
    footer: {
      tagline: 'Continue verbetering, doelgericht toegepast.',
      terms: 'Algemene voorwaarden',
      privacy: 'Privacybeleid',
      security: 'Beveiliging & AVG',
      method: 'KaizenKanri Method™ — alle rechten voorbehouden.',
      language: 'Geregistreerd en actief in Nederland. Product, live sessies en support zijn in het Engels; Nederlandse correspondentie, documentatie en contracten zijn schriftelijk beschikbaar. Elke wijziging hierin wordt op deze site aangekondigd.',
      trademark: 'Kanriva™ is een handelsmerk van Kaizen Kanri. FlowCrew™ is een handelsmerk van Kaizen Kanri.',
    },
  },
};
